/**
 * Shift Overlap Utilities
 *
 * Pure utility functions for checking shift time overlaps.
 * These utilities work with minimal shift data and have no UI dependencies.
 */

import { timeToMinutes } from '@zag/calendar/models';

/**
 * Minimal interface for a shift entry used in overlap calculations.
 * This interface is intentionally minimal to decouple from UI-specific types.
 */
export interface ShiftTimeEntry {
  startTime?: string;
  endTime?: string;
}

/**
 * Check if a shift crosses midnight (end time < start time)
 */
export function isOvernightShift(shift: ShiftTimeEntry): boolean {
  const start = timeToMinutes(shift.startTime);
  const end = timeToMinutes(shift.endTime);
  if (start === null || end === null) return false;
  return end < start; // e.g., 21:00 (1260) to 05:00 (300)
}

/**
 * Check if two shifts have overlapping time ranges
 */
export function doShiftsOverlap(shift1: ShiftTimeEntry, shift2: ShiftTimeEntry): boolean {
  const start1 = timeToMinutes(shift1.startTime);
  const end1 = timeToMinutes(shift1.endTime);
  const start2 = timeToMinutes(shift2.startTime);
  const end2 = timeToMinutes(shift2.endTime);

  // If we couldn't parse times, allow the drop (fail open)
  if (start1 === null || end1 === null || start2 === null || end2 === null) {
    return false;
  }

  // Helper to check if two ranges overlap or are adjacent (back-to-back)
  // Using <= because an employee can't end one shift and immediately start another
  const rangesOverlapOrAdjacent = (s1: number, e1: number, s2: number, e2: number): boolean => {
    return s1 <= e2 && s2 <= e1;
  };

  // Strict overlap check (for cross-midnight scenarios, don't count adjacent as overlap)
  const rangesOverlapStrict = (s1: number, e1: number, s2: number, e2: number): boolean => {
    return s1 < e2 && s2 < e1;
  };

  // Determine if shifts are overnight (end time < start time means it crosses midnight)
  const isOvernight1 = end1 < start1;
  const isOvernight2 = end2 < start2;

  // Adjust end times for overnight shifts (extend past midnight)
  const adjustedEnd1 = isOvernight1 ? end1 + 24 * 60 : end1;
  const adjustedEnd2 = isOvernight2 ? end2 + 24 * 60 : end2;

  // Check standard overlap (including adjacent for same-day shifts)
  if (rangesOverlapOrAdjacent(start1, adjustedEnd1, start2, adjustedEnd2)) {
    return true;
  }

  // If shift1 is overnight, also check shift2 in the "next day" context
  // This handles: shift1 = 22:00-06:00 (1320-1800), shift2 = 04:00-10:00 (240-600)
  // We need to check shift2 shifted to next day: 04:00-10:00 becomes 1680-2040
  // which overlaps with 1320-1800 from 1680-1800 (04:00-06:00 of next day)
  // Use strict overlap here - adjacent across midnight boundary is OK
  if (isOvernight1 && !isOvernight2) {
    const shift2NextDayStart = start2 + 24 * 60;
    const shift2NextDayEnd = end2 + 24 * 60;
    if (rangesOverlapStrict(start1, adjustedEnd1, shift2NextDayStart, shift2NextDayEnd)) {
      return true;
    }
  }

  // If shift2 is overnight, also check shift1 in the "next day" context
  // Use strict overlap here - adjacent across midnight boundary is OK
  if (isOvernight2 && !isOvernight1) {
    const shift1NextDayStart = start1 + 24 * 60;
    const shift1NextDayEnd = end1 + 24 * 60;
    if (rangesOverlapStrict(shift1NextDayStart, shift1NextDayEnd, start2, adjustedEnd2)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if an overnight shift (from previous day) overlaps with a shift on the current day.
 * The overnight shift's end time (e.g., 05:00) might overlap with or be adjacent to
 * the current day shift's start time (e.g., 05:00-13:00).
 */
export function doOvernightShiftOverlapWithMorning(overnightShift: ShiftTimeEntry, morningShift: ShiftTimeEntry): boolean {
  const overnightEnd = timeToMinutes(overnightShift.endTime);
  const morningStart = timeToMinutes(morningShift.startTime);
  const morningEnd = timeToMinutes(morningShift.endTime);

  if (overnightEnd === null || morningStart === null || morningEnd === null) {
    return false;
  }

  // The overnight shift ends at overnightEnd (e.g., 05:00 = 300 minutes)
  // The morning shift runs from morningStart to morningEnd (e.g., 05:00-13:00 = 300-780)
  // They overlap if the overnight end time is >= morning start time
  // Using >= because an employee can't end at 05:00 and immediately start at 05:00
  return overnightEnd >= morningStart && overnightEnd <= morningEnd;
}
